import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import endpoints from "../config";

const Glossary = () => {
  const { authToken, logout } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const [content, setContent] = useState(""); // raw HTML from backend
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const containerRef = useRef(null);
  const [matchCount, setMatchCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch glossary HTML
  useEffect(() => {
    const fetchGlossary = async () => {
      try {
        const res = await axios.get(endpoints.glossary.fetch, { headers });
        const htmlContent = res.data.html || "";

        const container = document.createElement("div");
        container.innerHTML = htmlContent;

        // Bootstrap table styling
        container.querySelectorAll("table").forEach((table) =>
          table.classList.add("table", "table-bordered", "table-striped", "table-hover", "text-center")
        );

        // Center headings
        container.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((h) =>
          h.classList.add("text-center", "my-2")
        );

        // Responsive images
        container.querySelectorAll("img").forEach((img) => {
          img.classList.add("img-fluid", "d-block", "mx-auto");
          img.style.maxWidth = "100%";
          img.style.height = "auto";
        });

        setContent(container.innerHTML);
      } catch (err) {
        if (err.response?.status === 401) logout();
        else toast.error("Failed to load glossary");
      } finally {
        setLoading(false);
      }
    };

    fetchGlossary();
  }, [headers, logout]);

  // Update match count whenever search or content changes
  useEffect(() => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch || trimmedSearch.length < 2) {
      setMatchCount(0);
      setCurrentIndex(0);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    let matches = 0;
    const regex = new RegExp(trimmedSearch, "gi");

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        matches += (node.textContent.match(regex) || []).length;
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== "IMG") {
        node.childNodes.forEach(walk);
      }
    };

    doc.body.childNodes.forEach(walk);
    setMatchCount(matches);
    setCurrentIndex(0);
  }, [search, content]);

  // Highlight text matches
  const getHighlightedHtml = () => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch || trimmedSearch.length < 2) return content;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    let matchIndex = -1;
    const regex = new RegExp(`(${trimmedSearch})`, "gi");

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (regex.test(node.textContent)) {
          node.replaceWith(
            ...node.textContent.split(regex).map((part) => {
              if (regex.test(part)) {
                matchIndex++;
                const mark = document.createElement("mark");
                mark.textContent = part;
                if (matchIndex === currentIndex) mark.classList.add("bg-warning");
                return mark;
              }
              return document.createTextNode(part);
            })
          );
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== "IMG") {
        node.childNodes.forEach(walk);
      }
    };

    doc.body.childNodes.forEach(walk);
    return doc.body.innerHTML;
  };

  // Scroll to active match
  useEffect(() => {
    if (!containerRef.current) return;
    const activeMark = containerRef.current.querySelector("mark.bg-warning");
    if (activeMark) activeMark.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentIndex, search]);

  const nextMatch = () => {
    if (matchCount === 0) return;
    setCurrentIndex((prev) => (prev + 1) % matchCount);
  };

  const prevMatch = () => {
    if (matchCount === 0) return;
    setCurrentIndex((prev) => (prev - 1 + matchCount) % matchCount);
  };

  return (
    <div className="container-fluid p-0" style={{ backgroundColor: "#d4edda2f"}}>
      <div className="container">

        {loading ? (
          <div className="alert alert-info">Loading glossary...</div>
        ) : (
          <div className="card  border-0 shadow-sm mx-auto col-12 col-md-10 position-relative mt-4 mb-4">
            {/* Sticky search and navigation */}
            <div className="sticky-top bg-green py-2 shadow-sm" style={{ top: "5rem", zIndex: 1040,backgroundColor:'#e1f7f2' }}>
              <div className="row align-items-center px-2">
                <div className="col-md-6">
                  <input
                    type="text"
                    placeholder="Search glossary..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="form-control"
                  />
                  {search.trim().length > 0 && search.trim().length < 2 && (
                    <div className="text-muted small mt-1">Enter at least 2 characters to search.</div>
                  )}
                </div>
                {matchCount > 0 && (
                  <div className="col-md-4 d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-secondary" onClick={prevMatch}>↑ Prev</button>
                    <button className="btn btn-outline-secondary" onClick={nextMatch}>↓ Next</button>
                    <span className="align-self-center text-muted">{currentIndex + 1}/{matchCount}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable content */}
            <div
              ref={containerRef}
              className="card-body overflow-auto"
              style={{ maxHeight: "80vh" }}
              dangerouslySetInnerHTML={{ __html: getHighlightedHtml() }}
            />

            {search.trim().length >= 2 && matchCount === 0 && (
              <div className="text-muted text-center mt-2">No matches found</div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Glossary;
